import React, { useEffect, useState } from 'react';
import { Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { apiErrorMessage } from '../api';
import { launchColors } from '../launchTheme';
import { alcoholApi } from './alcoholApi';
import { alcoholCommandId, counterColor } from './alcoholLabels';
import { AlcoholDetail, AlcoholDuplicate, AlcoholFacet, AlcoholFacets, NAME_LIMIT } from './alcoholTypes';

interface Props {
  open: boolean;
  facets: AlcoholFacets;
  defaults: { producer: string; category: string };
  onClose: () => void;
  onCreated: (detail: AlcoholDetail) => void;
}

const FreeText: React.FC<{ label: string; value: string; options: AlcoholFacet[]; onChange: (value: string) => void }> = ({ label, value, options, onChange }) => (
  <Autocomplete
    freeSolo
    options={options.map((item) => item.name)}
    inputValue={value}
    onInputChange={(_event, next) => onChange(next)}
    renderInput={(params) => <TextField {...params} size="small" label={label} />}
  />
);

const NewAlcoholDialog: React.FC<Props> = ({ open, facets, defaults, onClose, onCreated }) => {
  const [articleNumber, setArticleNumber] = useState('');
  const [name, setName] = useState('');
  const [producer, setProducer] = useState('');
  const [category, setCategory] = useState('');
  const [duplicates, setDuplicates] = useState<AlcoholDuplicate[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [commandId, setCommandId] = useState('');

  useEffect(() => {
    if (!open) return;
    setArticleNumber('');
    setName('');
    setProducer(defaults.producer);
    setCategory(defaults.category);
    setDuplicates([]);
    setError(null);
    setCommandId(alcoholCommandId());
  }, [defaults.category, defaults.producer, open]);

  const submit = async (allowDuplicate: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const fields: Record<string, string> = {};
      if (name.trim()) fields.name = name.trim();
      if (category.trim()) fields.category = category.trim();
      onCreated(await alcoholApi.create({ commandId, allowDuplicate, patch: { articleNumber, producer, fields } }));
    } catch (requestError: any) {
      const data = requestError?.response?.data;
      if (requestError?.response?.status === 409 && Array.isArray(data?.duplicates)) setDuplicates(data.duplicates);
      else setError(apiErrorMessage(requestError, 'Produkten kunde inte skapas.'));
    } finally {
      setBusy(false);
    }
  };

  const ready = Boolean(articleNumber.trim()) && name.length <= NAME_LIMIT;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Ny produkt</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Typography sx={{ fontSize: 13 }}>Produkten blir Kommande tills någon godkänner den. Resten fyller du i på produktkortet.</Typography>
          <TextField
            size="small"
            label="Artikelnummer"
            required
            autoFocus
            value={articleNumber}
            onChange={(event) => { setArticleNumber(event.target.value); setDuplicates([]); }}
            inputProps={{ maxLength: 30 }}
          />
          <TextField
            size="small"
            label="Produktnamn"
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={name.length > NAME_LIMIT}
            helperText={`${name.length}/${NAME_LIMIT}`}
            FormHelperTextProps={{ sx: { color: counterColor(name.length, NAME_LIMIT) } }}
            inputProps={{ maxLength: 120 }}
          />
          <FreeText label="Producent" value={producer} options={facets.producers} onChange={setProducer} />
          <FreeText label="Kategori" value={category} options={facets.categories} onChange={setCategory} />
          {duplicates.length > 0 && (
            <Alert severity="warning">
              Artikelnumret finns redan:
              {duplicates.map((item) => <div key={item.id}>{item.articleNumber} {item.name}</div>)}
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>Finns producenten inte läggs den till i producentregistret.</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Avbryt</Button>
        {duplicates.length > 0 ? (
          <Button variant="contained" disabled={busy || !ready} onClick={() => submit(true)}>Skapa ändå</Button>
        ) : (
          <Button variant="contained" disabled={busy || !ready} onClick={() => submit(false)}>Skapa</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NewAlcoholDialog;
